import { BigNum, MAX_LEVERAGE_ORDER_SIZE } from '@drift-labs/sdk';

/**
 * Checks if a given order amount represents an entire position order
 * by comparing it with MAX_LEVERAGE_ORDER_SIZE
 * @param orderAmount - The BigNum order amount to check
 * @returns true if the order is for the entire position, false otherwise
 */
export const isEntirePositionOrder = (orderAmount: BigNum): boolean => {
	const maxLeverageSize = new BigNum(
		MAX_LEVERAGE_ORDER_SIZE,
		orderAmount.precision
	);

	// Check if it matches the standard MAX_LEVERAGE_ORDER_SIZE
	const isMaxLeverage = Math.abs(maxLeverageSize.sub(orderAmount).toNum()) < 1;

	// Check if it matches the alternative large position size (using string to avoid number overflow)
	const alternativeMaxSize = new BigNum(
		'18446744072000000000',
		orderAmount.precision
	);
	const isAlternativeMax =
		Math.abs(alternativeMaxSize.sub(orderAmount).toNum()) < 1;

	return isMaxLeverage || isAlternativeMax;
};

/**
 * Gets the MAX_LEVERAGE_ORDER_SIZE as a BigNum with the same precision as the given amount
 * @param orderAmount - The BigNum order amount to match precision with
 * @returns BigNum representation of MAX_LEVERAGE_ORDER_SIZE
 */
export const getMaxLeverageOrderSize = (orderAmount: BigNum): BigNum => {
	return new BigNum(MAX_LEVERAGE_ORDER_SIZE, orderAmount.precision);
};

/**
 * Formats an order size for display, showing "Entire Position" if it's a max leverage order
 * @param orderAmount - The BigNum order amount to format
 * @param formatFn - Optional custom format function, defaults to prettyPrint()
 * @returns Formatted string showing either "Entire Position" or the formatted amount
 */
export const formatOrderSize = (
	orderAmount: BigNum,
	formatFn?: (amount: BigNum) => string
): string => {
	if (isEntirePositionOrder(orderAmount)) {
		return 'Entire Position';
	}
	return formatFn ? formatFn(orderAmount) : orderAmount.prettyPrint();
};
